import React, { useState, useMemo } from 'react';
import { Typography, Box } from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import FormLabel from "./FormLabel.jsx";
import { useUser } from '@/Context/UserContext.jsx';
import { CheckCircleIcon } from 'lucide-react';
export const FileSelector = ({
  label,
  fieldName,
  value,
  onChange,
  accept = '*/*',
  acceptLabel = '',
  multiple = false,
  sx = {},
  width = '',
  error: externalError = false,
  helperText: externalHelperText = '',
  required = false,
  allowedFileTypes,
}) => {

  const maxSize = 2 * 1024 * 1024; // 2 MB

  const fileTypeMap = {
    PDF: 'pdf',
    TXT: 'txt',
    CSV: 'csv',
    XLS: 'xls',
    XLSX: 'xlsx',
    DOC: 'doc',
    DOCX: 'docx',
    JPG: 'jpg',
    JPEG: 'jpeg',
    PNG: 'png',
    TIFF: 'tiff',
    ZIP: 'zip',
    RAR: 'rar',
  };

  const allowedExtensions = useMemo(() => {
    if (!allowedFileTypes) return Object.values(fileTypeMap);
    return allowedFileTypes
      .split(',')
      .map(ft => ft.trim().toUpperCase())
      .map(ft => fileTypeMap[ft])
      .filter(Boolean);
  }, [allowedFileTypes]);

  const { loading}  =useUser();

  const inputAccept = useMemo(() => {
    if (!allowedFileTypes) return '*/*';
    return allowedFileTypes
      .split(',')
      .map(ft => ft.trim().toUpperCase())
      .map(ft => fileTypeMap[ft])
      .filter(Boolean)
      .map(ext => '.' + ext)   // convert 'pdf' → '.pdf'
      .join(',');
  }, [allowedFileTypes]);

  const [localError, setLocalError] = useState(false);
  const [localHelperText, setLocalHelperText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]); // 👈 store selected files for UI
  const isValidType = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    return allowedExtensions.includes(ext);
  };
  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      return;
    }

    let validFiles = Array.from(files).filter(file => {
      if (!isValidType(file)) {
        setLocalError(true);
        setLocalHelperText(`Invalid file type: ${file.name}`);
        return false;
      }
      if (file.size > maxSize) {
        setLocalError(true);
        setLocalHelperText(`File too large: ${file.name} (max 2 MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      return; // no valid files
    }
    setLocalError(false);
    setLocalHelperText('');
    // Update selected files for UI
    setSelectedFiles(validFiles);
    if (multiple) {
      onChange(fieldName, validFiles);
    } else {
      onChange(fieldName, validFiles[0]);
    }
  };

  const displayedAcceptLabel =
    acceptLabel || (accept === '*/*' ? 'max size 2 MB' : accept.split(',').join(', '));

  // const allowedFileTypes = "PDF, TXT, CSV, XLS, XLSX, DOC, DOCX, JPG, JPEG, PNG, TIFF, ZIP, RAR";

  const showError = externalError || localError;
  const showHelperText = externalHelperText || localHelperText;

// In your FileSelector component, update the labelText logic:
const labelText = (() => {
  if (loading) {
    return <>Uploading...</>;
  }

  if (multiple) {
    return Array.isArray(value) && value.length > 0
      ? value.map((f, i) => {
          if (typeof f === "object") {
            return (
              <span key={i} className="flex items-center gap-1">
                {f.fileName || f.name || "File"}
              </span>
            );
          } else {
            return (
              <span key={i} className="flex items-center justify-center gap-3 text-green-600">
                <CheckCircleIcon fontSize="small" color='green' />
                Uploaded Successfully
              </span>
            );
          }
        })
      : "Choose files";
  } else {
    if (!value) return "Choose file";

    return typeof value === "object" ? (
      <span className="flex items-center gap-1">
        {value.fileName || value.name || "Choose file"}
      </span>
    ) : (
      <span className="flex items-center justify-center gap-7 text-green-600">
                Uploaded Successfully
      </span>
    );
  }
})();

  return (
    <Box sx={{ width:"100%", ...sx }}>
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: "var(--v-gap-sm)" }}>
          <FormLabel label={label} required={required} />
          {displayedAcceptLabel && (
            <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: 'text.disabled' }}>
              ({displayedAcceptLabel})
            </Typography>
          )}
        </Box>
      )}



      {/* Entire dashed box clickable */}
      <label htmlFor={`file-upload-${fieldName}`} style={{ display: 'block', cursor: 'pointer' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: '1px dashed',
            borderColor: showError ? 'error.main' : 'divider',
            borderRadius: 1,
            p: 1.5,
            '&:hover': {
              borderColor: showError ? 'error.main' : 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
        >
          <UploadIcon fontSize="small" color={showError ? 'error' : 'action'} />
          <Typography variant="body2">{labelText}</Typography>
        </Box>
      </label>

      <input
        type="file"
        id={`file-upload-${fieldName}`}
        // accept={accept}
        accept={inputAccept}
        multiple={multiple}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />


      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}>
        Allowed file types: {allowedFileTypes} | Max size: 2 MB
      </Typography>

      {showError && (
        <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
          {showHelperText}
        </Typography>
      )}
    </Box>
  );
};
